import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Trash2, Plus, Package, Upload, List, ScanBarcode, Loader2 } from 'lucide-react';
import POBulkUploadTab from './POBulkUploadTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const UNITS = ['PCS', 'KG', 'BOX', 'CASE', 'GRAMS', 'LITRE'];
const GST_SLABS = [0, 5, 12, 18, 28, 40];

// PO rate convention (May 2026): the value typed into the "Rate" input is
// treated as INCLUSIVE-OF-GST. Internally we derive the EXCL-GST rate for the
// amount_before_tax math and convert back to EXCL-GST in the payload sent to
// the backend (which continues to store/use rate as excl-GST). Conversions are
// safe for gst=0 → no change.
function rateInclToExcl(rateIncl, gstPct) {
  const g = Number(gstPct) || 0;
  return g > 0 ? Number(rateIncl) / (1 + g / 100) : Number(rateIncl) || 0;
}
function rateExclToIncl(rateExcl, gstPct) {
  const g = Number(gstPct) || 0;
  return g > 0 ? Number(rateExcl) * (1 + g / 100) : Number(rateExcl) || 0;
}

function POItemsTab({ items, setItems, products, disabled, userRole = 'staff', onBulkSupplierGroups }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [entryMode, setEntryMode] = useState('manual'); // 'manual' or 'bulk'
  
  // Barcode scanner state
  const [scannerBarcode, setScannerBarcode] = useState('');
  const [scannerLoading, setScannerLoading] = useState(false);
  const scannerInputRef = useRef(null);
  
  // Check if bulk upload is allowed (admin and manager only)
  const canBulkUpload = ['admin', 'manager'].includes(userRole);
  
  const filteredProducts = products.filter(p => {
    const term = searchTerm.toLowerCase();
    return p.name.toLowerCase().indexOf(term) >= 0 ||
      (p.product_id && p.product_id.toLowerCase().indexOf(term) >= 0) ||
      (p.barcode && p.barcode.toLowerCase().indexOf(term) >= 0) ||
      (p.sku && p.sku.toLowerCase().indexOf(term) >= 0);
  }).slice(0, 10);
  
  // Barcode scanner handler
  async function handleBarcodeScan() {
    const code = scannerBarcode.trim();
    if (!code || scannerLoading) return;
    
    setScannerLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products/barcode/${encodeURIComponent(code)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const product = response.data;
      
      // Check if product already in list
      const existingIdx = items.findIndex(i => i.product_id === product.id);
      if (existingIdx >= 0) {
        // Increment quantity
        const newItems = [...items];
        const item = { ...newItems[existingIdx] };
        item.quantity = Number(item.quantity) + 1;
        const qty = item.quantity;
        const rateIncl = Number(item.purchase_rate) || 0;  // user-entered INCL-GST
        const disc = Number(item.discount_percentage) || 0;
        const gst = Number(item.gst_percentage) || 0;
        const rateExcl = rateInclToExcl(rateIncl, gst);
        const amountBeforeDiscount = qty * rateExcl;
        const discountAmount = amountBeforeDiscount * disc / 100;
        const amountBeforeTax = amountBeforeDiscount - discountAmount;
        const gstAmount = amountBeforeTax * gst / 100;
        item.amount_before_tax = amountBeforeTax;
        item.gst_amount = gstAmount;
        item.line_total = amountBeforeTax + gstAmount;
        newItems[existingIdx] = item;
        setItems(newItems);
        playBeep(true);
        toast.success(`${product.name} — qty increased to ${item.quantity}`);
      } else {
        // Add new item
        addProduct({
          id: product.id,
          name: product.name,
          sku: product.product_id,
          product_id: product.product_id,
          inward_b2b_price: product.purchase_price || product.inward_price_excl_gst || 0,
          inward_price_incl_gst: product.inward_price_incl_gst,
          mrp: product.mrp,
          outward_selling_price: product.outward_selling_price,
          purchase_price: product.purchase_price || 0,
          gst_percentage: product.gst_percentage ?? 18,
          measurement_unit: product.measurement_unit || 'PCS',
          hsn_code: product.hsn_code,
          current_stock: product.current_stock ?? 0,
        });
        playBeep(true);
        toast.success(`Added: ${product.name}`);
      }
    } catch (err) {
      playBeep(false);
      if (err.response?.status === 404) {
        toast.error('Product not found', { description: `No product with barcode: ${code}` });
      } else {
        toast.error('Barcode lookup failed');
      }
    } finally {
      setScannerBarcode('');
      setScannerLoading(false);
      scannerInputRef.current?.focus();
    }
  }
  
  function handleScannerKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeScan();
    }
  }
  
  function playBeep(success) {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = success ? 800 : 300;
      osc.type = 'sine';
      gain.gain.value = 0.1;
      osc.start();
      osc.stop(ctx.currentTime + (success ? 0.1 : 0.3));
    } catch (e) {
      // AudioContext not supported in this browser — silent fallback by design
      console.debug('Barcode beep skipped:', e?.message);
    }
  }
  
  function addProduct(product) {
    const qty = 1;
    // Use ?? so a legitimate 0% GST product isn't silently bumped to 18%.
    const gst = product.gst_percentage ?? 18;
    // Rate input is now INCL-GST. Prefer master's incl-GST price if set;
    // otherwise convert the excl-GST master price up to incl-GST.
    const rateExclMaster = product.inward_b2b_price || product.purchase_price || 0;
    const rate = product.inward_price_incl_gst
      || rateExclToIncl(rateExclMaster, gst);
    const rateExcl = rateInclToExcl(rate, gst);
    const amountBeforeTax = qty * rateExcl;
    const gstAmount = amountBeforeTax * gst / 100;
    
    setItems([...items, {
      _uid: `itm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku || product.product_id || '',
      warehouse_stock: product.current_stock ?? 0,
      // ── Snapshot of live master pricing at add-time. These stay stable
      // for the life of this PO draft; they're a decision-support display
      // only, not part of the payload sent to the backend.
      master_mrp: Number(product.mrp) || 0,
      master_landing_incl_gst: Number(product.inward_price_incl_gst) || 0,
      master_selling_price: Number(product.outward_selling_price) || 0,
      quantity: qty,
      unit: product.measurement_unit || 'PCS',
      purchase_rate: rate,
      discount_percentage: 0,
      gst_percentage: gst,
      amount_before_tax: amountBeforeTax,
      gst_amount: gstAmount,
      line_total: amountBeforeTax + gstAmount,
    }]);
    setSearchTerm('');
    setShowSearch(false);
  }
  
  function updateItem(index, field, value) {
    const newItems = [...items];
    const item = {...newItems[index]};
    item[field] = value;
    
    const qty = Number(item.quantity) || 0;
    const rateIncl = Number(item.purchase_rate) || 0;  // user-entered INCL-GST
    const disc = Number(item.discount_percentage) || 0;
    const gst = Number(item.gst_percentage) || 0;
    
    const rateExcl = rateInclToExcl(rateIncl, gst);
    const amountBeforeDiscount = qty * rateExcl;
    const discountAmount = amountBeforeDiscount * disc / 100;
    const amountBeforeTax = amountBeforeDiscount - discountAmount;
    const gstAmount = amountBeforeTax * gst / 100;
    
    item.amount_before_tax = amountBeforeTax;
    item.gst_amount = gstAmount;
    item.line_total = amountBeforeTax + gstAmount;
    
    newItems[index] = item;
    setItems(newItems);
  }
  
  // Build stock-lookup map keyed by product_id for O(1) display fallback
  // (items created via bulk upload or older PO edits may not carry warehouse_stock).
  // Same map also serves master MRP / Landing / Selling for the read-only
  // pricing columns so we never fall back to legacy stale fields on the item.
  const productLookup = React.useMemo(() => {
    const m = {};
    (products || []).forEach(p => {
      m[p.id] = {
        stock: p.current_stock ?? 0,
        mrp: Number(p.mrp) || 0,
        landing: Number(p.inward_price_incl_gst) || 0,
        selling: Number(p.outward_selling_price) || 0,
      };
    });
    return m;
  }, [products]);

  function getItemStock(item) {
    if (item.warehouse_stock !== undefined && item.warehouse_stock !== null) {
      return Number(item.warehouse_stock);
    }
    return productLookup[item.product_id]?.stock ?? 0;
  }

  // Return current-live master values for an item — either from the snapshot
  // taken at add-time, or via the products list for legacy/bulk rows that
  // didn't capture the snapshot.
  function getItemMaster(item) {
    const p = productLookup[item.product_id] || {};
    return {
      mrp:     Number(item.master_mrp)             || p.mrp     || 0,
      landing: Number(item.master_landing_incl_gst) || p.landing || 0,
      selling: Number(item.master_selling_price)   || p.selling || 0,
    };
  }

  // Effective per-unit landing implied by the user's typed rate + discount
  // (rate is INCL-GST already). Used to preview post-GRN margin impact.
  function getEffectiveLanding(item) {
    const rate = Number(item.purchase_rate) || 0;
    const disc = Number(item.discount_percentage) || 0;
    return rate - (rate * disc / 100);
  }

  // Colour-coded margin band. Same thresholds for both inward + outward.
  function marginBadgeClass(pct) {
    if (!isFinite(pct)) return 'bg-slate-100 text-slate-500 border-slate-200';
    if (pct < 5) return 'bg-red-50 text-red-700 border-red-200';
    if (pct < 15) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }

  function fmtPct(pct) {
    if (!isFinite(pct)) return '—';
    return `${pct.toFixed(2)}%`;
  }

  // Static "current live" margin (uses master's landing) + preview margin
  // (uses buyer's effective typed rate). If the two match to 2 decimals we
  // only show the current value, no preview badge.
  function computeMargins(item) {
    const m = getItemMaster(item);
    const eff = getEffectiveLanding(item);
    const inwardCurrent  = m.mrp     > 0 ? ((m.mrp     - m.landing) / m.mrp)     * 100 : NaN;
    const outwardCurrent = m.selling > 0 ? ((m.selling - m.landing) / m.selling) * 100 : NaN;
    const inwardPreview  = m.mrp     > 0 ? ((m.mrp     - eff)       / m.mrp)     * 100 : NaN;
    const outwardPreview = m.selling > 0 ? ((m.selling - eff)       / m.selling) * 100 : NaN;
    const previewDiffers = Math.abs(eff - m.landing) > 0.01;
    return { m, eff, inwardCurrent, outwardCurrent, inwardPreview, outwardPreview, previewDiffers };
  }
  
  function removeItem(index) {
    setItems(items.filter((_, i) => i !== index));
  }
  
  // Handle items imported from bulk upload
  function handleBulkImport(importedItems, supplierGroups) {
    // Snapshot master pricing on each imported item so the read-only
    // MRP/Landing/Selling/Margin columns render immediately without
    // waiting for the productLookup fallback path.
    const enriched = (importedItems || []).map((it) => {
      if (it.master_mrp !== undefined) return it;  // already snapshotted
      const p = productLookup[it.product_id];
      if (!p) return it;
      return {
        ...it,
        master_mrp: p.mrp,
        master_landing_incl_gst: p.landing,
        master_selling_price: p.selling,
        warehouse_stock: it.warehouse_stock ?? p.stock,
      };
    });
    setItems([...items, ...enriched]);
    setEntryMode('manual'); // Switch back to manual to show items
    if (onBulkSupplierGroups) {
      onBulkSupplierGroups(supplierGroups);
    }
  }
  
  return (
    <div className="space-y-4" data-testid="po-items-tab">
      {/* Entry Mode Tabs - Only show if bulk upload is allowed */}
      {canBulkUpload && !disabled && (
        <Tabs value={entryMode} onValueChange={setEntryMode} className="w-full">
          <TabsList className="grid grid-cols-2 w-64">
            <TabsTrigger value="manual" className="gap-2">
              <List className="w-4 h-4" />
              Manual Entry
            </TabsTrigger>
            <TabsTrigger value="bulk" className="gap-2" data-testid="bulk-upload-tab-trigger">
              <Upload className="w-4 h-4" />
              Bulk Upload
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="bulk" className="mt-4">
            <POBulkUploadTab
              onItemsImported={handleBulkImport}
              disabled={disabled}
            />
          </TabsContent>
          
          <TabsContent value="manual" className="mt-4">
            {/* Manual Entry Content - rendered below */}
          </TabsContent>
        </Tabs>
      )}
      
      {/* Manual Entry Mode or when bulk not allowed */}
      {(entryMode === 'manual' || !canBulkUpload) && (
        <>
          {/* Barcode Scanner */}
          {!disabled && (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="po-barcode-scanner">
              <div className="flex items-center gap-2 mb-2">
                <ScanBarcode className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-slate-700">Barcode Scanner</span>
                <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700">Scan to add</Badge>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    ref={scannerInputRef}
                    type="text"
                    value={scannerBarcode}
                    onChange={(e) => setScannerBarcode(e.target.value)}
                    onKeyDown={handleScannerKeyDown}
                    placeholder="Scan or type barcode, then press Enter..."
                    className="pl-10 font-mono"
                    disabled={scannerLoading}
                    data-testid="po-barcode-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleBarcodeScan}
                  disabled={scannerLoading || !scannerBarcode.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                  data-testid="po-barcode-add-btn"
                >
                  {scannerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">Scan product barcode to instantly add it. Scanning same product again increases quantity.</p>
            </div>
          )}

          {/* Product Search */}
          {!disabled && (
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium">Add Products</span>
              </div>
              <Search className="absolute left-3 top-[42px] h-4 w-4 text-gray-400" />
              <Input
                data-testid="product-search-input"
                placeholder="Search products by name, SKU, barcode..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setShowSearch(e.target.value.length > 0);
                }}
                onFocus={() => setShowSearch(searchTerm.length > 0)}
                className="pl-10"
              />
              {showSearch && filteredProducts.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-96 overflow-y-auto">
                  {filteredProducts.map(product => {
                    const mrp = Number(product.mrp) || 0;
                    const landing = Number(product.inward_price_incl_gst) || 0;
                    const selling = Number(product.outward_selling_price) || 0;
                    const inMgn = mrp > 0 ? ((mrp - landing) / mrp) * 100 : NaN;
                    const outMgn = selling > 0 ? ((selling - landing) / selling) * 100 : NaN;
                    return (
                    <button
                      type="button"
                      key={product.id}
                      data-testid={`product-option-${product.id}`}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 flex justify-between items-start gap-3 border-b last:border-b-0"
                      onClick={() => addProduct(product)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{product.name}</div>
                        <div className="text-xs text-gray-500">
                          {product.product_id} {product.barcode ? `| BC: ${product.barcode}` : ''} | HSN: {product.hsn_code} | GST: {product.gst_percentage}%
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-2 text-[11px]">
                          <span className="px-1.5 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200" data-testid={`search-mrp-${product.id}`}>
                            MRP ₹{mrp.toFixed(2)}
                          </span>
                          <span className="px-1.5 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200" data-testid={`search-landing-${product.id}`}>
                            Landing ₹{landing.toFixed(2)}
                          </span>
                          <span className="px-1.5 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200" data-testid={`search-selling-${product.id}`}>
                            Sell ₹{selling.toFixed(2)}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded border ${marginBadgeClass(inMgn)}`} data-testid={`search-inward-margin-${product.id}`}>
                            In-Mgn {fmtPct(inMgn)}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded border ${marginBadgeClass(outMgn)}`} data-testid={`search-outward-margin-${product.id}`}>
                            Out-Mgn {fmtPct(outMgn)}
                          </span>
                          <span className="px-1.5 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">
                            Stock {product.current_stock ?? 0}
                          </span>
                        </div>
                      </div>
                      <Plus className="h-4 w-4 text-blue-600 mt-1 shrink-0" />
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Items Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1600px]">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700 min-w-[180px]">Product</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 w-24" data-testid="th-warehouse-stock">Warehouse Stock</th>
                    <th className="px-3 py-3 text-right font-semibold text-gray-700 w-24" data-testid="th-mrp">MRP</th>
                    <th className="px-3 py-3 text-right font-semibold text-gray-700 w-28" data-testid="th-landing" title="Current Landing Price (Inward B2B, inclusive of GST) from Master Catalogue">Landing (incl GST)</th>
                    <th className="px-3 py-3 text-right font-semibold text-gray-700 w-24" data-testid="th-selling" title="Current Outward B2B Selling Price from Master Catalogue">Selling</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 w-24" data-testid="th-inward-margin" title="Inward Margin: Margin between Current Landing Price and MRP. Preview badge appears if this PO's effective landing differs from master.">In. Mgn %</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 w-24" data-testid="th-outward-margin" title="Outward Margin: Margin between Current Landing Price and Current Selling Price. Preview badge appears if this PO's effective landing differs from master.">Out. Mgn %</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 w-24">Qty</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 w-20">Unit</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 w-28" title="Rate inclusive of GST">Rate (incl. GST)</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 w-16">Disc%</th>
                    <th className="px-3 py-3 text-right font-semibold text-gray-700 w-24">Disc. Price</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 w-16">GST%</th>
                    <th className="px-3 py-3 text-right font-semibold text-gray-700 w-24">GST Amt</th>
                    <th className="px-3 py-3 text-right font-semibold text-gray-700 w-28">Total</th>
                    {!disabled && <th className="px-3 py-3 w-10"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={disabled ? 15 : 16} className="px-4 py-12 text-center text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No items added yet</p>
                        <p className="text-xs mt-1">
                          {canBulkUpload ? 'Search and add products or use Bulk Upload' : 'Search and add products above'}
                        </p>
                      </td>
                    </tr>
                  )}
                  {items.map((item, idx) => {
                    const marg = computeMargins(item);
                    return (
                    <tr key={item._uid || `${item.product_id}-${idx}`} data-testid={`po-item-row-${idx}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="font-medium">{item.product_name}</div>
                        <div className="text-xs text-gray-500">{item.product_sku}</div>
                      </td>
                      <td className="px-3 py-2 text-center" data-testid={`item-warehouse-stock-${idx}`}>
                        {(() => {
                          const stk = getItemStock(item);
                          const low = stk < 10;
                          return (
                            <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-sm font-semibold ${low ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-slate-50 text-slate-700 border border-slate-200'}`}>
                              {stk}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700" data-testid={`item-mrp-${idx}`}>
                        {marg.m.mrp > 0 ? `₹${marg.m.mrp.toFixed(2)}` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700" data-testid={`item-landing-${idx}`}>
                        {marg.m.landing > 0 ? `₹${marg.m.landing.toFixed(2)}` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700" data-testid={`item-selling-${idx}`}>
                        {marg.m.selling > 0 ? `₹${marg.m.selling.toFixed(2)}` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-2 py-2 text-center" data-testid={`item-inward-margin-${idx}`}>
                        <span className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold ${marginBadgeClass(marg.inwardCurrent)}`}
                              title="Margin between Current Landing Price and MRP.">
                          {fmtPct(marg.inwardCurrent)}
                        </span>
                        {marg.previewDiffers && marg.m.mrp > 0 && (
                          <div className={`mt-1 inline-block px-1.5 py-0.5 rounded border text-[10px] font-medium ${marginBadgeClass(marg.inwardPreview)}`}
                               title="Preview: margin if this PO is completed at the entered rate."
                               data-testid={`item-inward-margin-preview-${idx}`}>
                            new {fmtPct(marg.inwardPreview)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center" data-testid={`item-outward-margin-${idx}`}>
                        <span className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold ${marginBadgeClass(marg.outwardCurrent)}`}
                              title="Margin between Current Landing Price and Current Selling Price.">
                          {fmtPct(marg.outwardCurrent)}
                        </span>
                        {marg.previewDiffers && marg.m.selling > 0 && (
                          <div className={`mt-1 inline-block px-1.5 py-0.5 rounded border text-[10px] font-medium ${marginBadgeClass(marg.outwardPreview)}`}
                               title="Preview: margin if this PO is completed at the entered rate."
                               data-testid={`item-outward-margin-preview-${idx}`}>
                            new {fmtPct(marg.outwardPreview)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <Input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.quantity}
                          onChange={e => updateItem(idx, 'quantity', e.target.value)}
                          className="h-8 text-center"
                          disabled={disabled}
                          data-testid={`item-qty-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <Select 
                          value={item.unit} 
                          onValueChange={v => updateItem(idx, 'unit', v)}
                          disabled={disabled}
                        >
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.purchase_rate}
                          onChange={e => updateItem(idx, 'purchase_rate', e.target.value)}
                          className="h-8 text-center"
                          disabled={disabled}
                          data-testid={`item-rate-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={item.discount_percentage}
                          onChange={e => updateItem(idx, 'discount_percentage', e.target.value)}
                          className="h-8 text-center"
                          disabled={disabled}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        {(() => {
                          const rate = Number(item.purchase_rate) || 0;
                          const disc = Number(item.discount_percentage) || 0;
                          const discPrice = rate - (rate * disc / 100);
                          return disc > 0 ? (
                            <div>
                              <span className="font-semibold text-blue-700">₹{discPrice.toFixed(2)}</span>
                              <span className="block text-xs text-gray-400 line-through">₹{rate.toFixed(2)}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-1">
                        <Select 
                          value={String(item.gst_percentage)} 
                          onValueChange={v => updateItem(idx, 'gst_percentage', v)}
                          disabled={disabled}
                        >
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {GST_SLABS.map(g => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600">
                        ₹{(item.gst_amount || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-green-700">
                        ₹{(item.line_total || 0).toFixed(2)}
                      </td>
                      {!disabled && (
                        <td className="px-2 py-1 text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(idx)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`remove-item-${idx}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Item Summary */}
          {items.length > 0 && (
            <div className="flex justify-end">
              <div className="text-sm text-gray-600">
                Total Items: <span className="font-semibold">{items.length}</span> | 
                Total Qty: <span className="font-semibold">{items.reduce((s, i) => s + Number(i.quantity), 0)}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default POItemsTab;
export { rateInclToExcl, rateExclToIncl };
